import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import ServiceCard from "./ServiceCard";
import { fadeUp, softReveal, staggerParent, viewportOnce } from "./motion";
import { servicesData } from "../data/services";

const Services = () => {
  const navigate = useNavigate();
  const [activeService, setActiveService] = useState(null);
  const [supportsHover, setSupportsHover] = useState(false);
  const serviceRefs = useRef({});
  const serviceListRef = useRef(null);
  const hoverOpenTimeoutRef = useRef(null);
  const hoverCloseTimeoutRef = useRef(null);
  const rowSize = 3;

  const clearHoverTimers = () => {
    if (hoverOpenTimeoutRef.current) {
      window.clearTimeout(hoverOpenTimeoutRef.current);
      hoverOpenTimeoutRef.current = null;
    }

    if (hoverCloseTimeoutRef.current) {
      window.clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    const updateSupportsHover = () => {
      setSupportsHover(mediaQuery.matches);
    };

    updateSupportsHover();
    mediaQuery.addEventListener("change", updateSupportsHover);

    return () => {
      mediaQuery.removeEventListener("change", updateSupportsHover);
    };
  }, []);

  useEffect(() => () => clearHoverTimers(), []);

  return (
    <motion.section
      className="services"
      variants={staggerParent(0.06, 0.12)}
      initial="hidden"
      whileInView="show"
      viewport={viewportOnce}
    >
      <motion.div className="services-header" variants={fadeUp}>
        <p className="section-kicker">Services</p>
        <h1>Digital Marketing Services for Businesses That Want Better Leads</h1>
        <p className="desktop-copy">
          We offer practical digital marketing services for businesses that want stronger visibility, better enquiries, and clearer results from online efforts, with local support from Madhapur, Hyderabad.
        </p>
        <p className="mobile-copy">
          Practical services for stronger visibility, better enquiries, and clearer digital marketing results.
        </p>
      </motion.div>

      <motion.div
        ref={serviceListRef}
        className="service-list"
        variants={staggerParent(0.12, 0.09)}
      >
        {servicesData.map((service, index) => (
          <ServiceCard
            key={service.title}
            {...service}
            expanded={activeService === service.title}
            rootRef={(node) => {
              if (node) {
                serviceRefs.current[service.title] = node;
              }
            }}
            onHoverStart={() => {
              if (!supportsHover) {
                setActiveService(service.title);
                return;
              }

              if (hoverCloseTimeoutRef.current) {
                window.clearTimeout(hoverCloseTimeoutRef.current);
                hoverCloseTimeoutRef.current = null;
              }

              if (hoverOpenTimeoutRef.current) {
                window.clearTimeout(hoverOpenTimeoutRef.current);
              }

              hoverOpenTimeoutRef.current = window.setTimeout(() => {
                setActiveService(service.title);

                if (serviceRefs.current[service.title] && serviceListRef.current) {
                  const navbarOffset = 96;
                  const rowStartIndex = Math.floor(index / rowSize) * rowSize;
                  const rowAnchorService = servicesData[rowStartIndex];
                  const rowAnchorCard = serviceRefs.current[rowAnchorService.title];
                  const targetTop =
                    rowAnchorCard.getBoundingClientRect().top +
                    window.scrollY -
                    navbarOffset;

                  window.scrollTo({
                    top: Math.max(0, targetTop),
                    behavior: "smooth",
                  });
                }

                hoverOpenTimeoutRef.current = null;
              }, 90);
            }}
            onHoverEnd={() => {
              if (!supportsHover) {
                setActiveService((current) =>
                  current === service.title ? null : current
                );
                return;
              }

              if (hoverOpenTimeoutRef.current) {
                window.clearTimeout(hoverOpenTimeoutRef.current);
                hoverOpenTimeoutRef.current = null;
              }

              if (hoverCloseTimeoutRef.current) {
                window.clearTimeout(hoverCloseTimeoutRef.current);
              }

              hoverCloseTimeoutRef.current = window.setTimeout(() => {
                setActiveService((current) =>
                  current === service.title ? null : current
                );
                hoverCloseTimeoutRef.current = null;
              }, 160);
            }}
            onToggle={() =>
              setActiveService((current) =>
                current === service.title ? null : service.title
              )
            }
          />
        ))}
      </motion.div>

      <motion.div className="service-link-grid" variants={staggerParent(0.08, 0.06)}>
        {servicesData.map((service) => (
          <motion.div key={service.slug} variants={fadeUp}>
            <Link className="service-page-link" to={`/services/${service.slug}`}>
              <span>{service.shortTitle}</span>
              <strong>View service page</strong>
            </Link>
          </motion.div>
        ))}
      </motion.div>

      <motion.div className="services-cta-band" variants={softReveal} whileHover={{ scale: 1.01 }}>
        <h3>Need a digital marketing plan that fits your business goals?</h3>
        <button className="cta-button cta-primary" onClick={() => navigate("/contact")}>
          Get Started
        </button>
      </motion.div>
    </motion.section>
  );
};

export default Services;
